import * as THREE from 'three';
import type { FpsParticleSystem } from './FpsParticleSystem.js';

const PLANE_Y = 55;
const PLANE_SPEED = 38;
const PATH_HALF = 95;

type ContrailPuff = {
  mesh: THREE.Mesh;
  life: number;
  maxLife: number;
};

/**
 * Single flyover plane before a supply crate is released — one plane per drop spawn.
 */
export class FpsSupplyDropPlane {
  private readonly root = new THREE.Group();
  private readonly pathStart: THREE.Vector3;
  private readonly pathEnd: THREE.Vector3;
  private readonly dropX: number;
  private readonly dropZ: number;
  private elapsed = 0;
  private passed = false;
  private done = false;
  private trailAcc = 0;
  private readonly trailPuffs: ContrailPuff[] = [];
  private readonly trailGeo = new THREE.SphereGeometry(0.18, 6, 6);
  private readonly trailMat = new THREE.MeshBasicMaterial({
    color: 0xddeeff,
    transparent: true,
    opacity: 0.42,
    depthWrite: false,
  });

  constructor(
    sceneRoot: THREE.Group,
    dropX: number,
    dropZ: number,
    private readonly onPassedDrop: () => void,
    private readonly particles?: FpsParticleSystem,
  ) {
    this.dropX = dropX;
    this.dropZ = dropZ;
    this.pathStart = new THREE.Vector3(dropX - PATH_HALF, PLANE_Y, dropZ - 18);
    this.pathEnd = new THREE.Vector3(dropX + PATH_HALF, PLANE_Y, dropZ + 18);

    const fuselage = new THREE.Mesh(
      new THREE.BoxGeometry(1.4, 0.55, 4.8),
      new THREE.MeshStandardMaterial({
        color: 0x8899aa,
        metalness: 0.55,
        roughness: 0.38,
      }),
    );
    fuselage.castShadow = true;

    const wing = new THREE.Mesh(
      new THREE.BoxGeometry(7.2, 0.12, 1.4),
      new THREE.MeshStandardMaterial({ color: 0x667788, metalness: 0.45, roughness: 0.42 }),
    );
    wing.position.y = -0.08;

    const tail = new THREE.Mesh(
      new THREE.BoxGeometry(0.12, 1.1, 1.05),
      new THREE.MeshStandardMaterial({ color: 0x778899, metalness: 0.4, roughness: 0.45 }),
    );
    tail.position.set(0, 0.42, 2.05);

    const prop = new THREE.Mesh(
      new THREE.CylinderGeometry(0.04, 0.04, 0.55, 8),
      new THREE.MeshStandardMaterial({ color: 0x334455, metalness: 0.6, roughness: 0.35 }),
    );
    prop.rotation.z = Math.PI / 2;
    prop.position.set(0, 0, -2.55);

    this.root.add(fuselage, wing, tail, prop);
    this.root.position.copy(this.pathStart);
    const tangent = new THREE.Vector3().subVectors(this.pathEnd, this.pathStart).normalize();
    this.root.lookAt(this.pathStart.clone().add(tangent));
    sceneRoot.add(this.root);
  }

  update(dt: number): boolean {
    if (this.done) return true;
    if (!Number.isFinite(dt) || dt <= 0) return false;

    this.elapsed += dt;
    const pathLen = this.pathStart.distanceTo(this.pathEnd);
    const dist = Math.min(pathLen, PLANE_SPEED * this.elapsed);
    const t = pathLen > 1e-4 ? dist / pathLen : 1;
    this.root.position.lerpVectors(this.pathStart, this.pathEnd, t);

    this.trailAcc += dt;
    if (this.trailAcc > 0.055) {
      this.trailAcc = 0;
      this.spawnContrail();
    }
    this.updateContrail(dt);

    if (!this.passed) {
      const tangent = new THREE.Vector3().subVectors(this.pathEnd, this.pathStart).normalize();
      const toDrop = new THREE.Vector3(this.dropX - this.root.position.x, 0, this.dropZ - this.root.position.z);
      if (toDrop.dot(tangent) <= 0) {
        this.passed = true;
        this.onPassedDrop();
      }
    }

    if (t >= 1) {
      this.done = true;
      this.root.visible = false;
      return true;
    }
    return false;
  }

  isDone(): boolean {
    return this.done;
  }

  private spawnContrail(): void {
    const pos = this.root.position.clone();
    pos.y -= 0.35 + Math.random() * 0.25;
    pos.x += (Math.random() - 0.5) * 0.6;
    pos.z += (Math.random() - 0.5) * 0.6;
    if (this.particles) {
      this.particles.emitPlaneContrail(pos);
      return;
    }
    const mesh = new THREE.Mesh(this.trailGeo, this.trailMat);
    mesh.position.copy(pos);
    this.root.parent?.add(mesh);
    this.trailPuffs.push({ mesh, life: 0, maxLife: 0.75 + Math.random() * 0.35 });
  }

  private updateContrail(dt: number): void {
    for (let i = this.trailPuffs.length - 1; i >= 0; i--) {
      const p = this.trailPuffs[i]!;
      p.life += dt;
      const t = p.life / p.maxLife;
      const mat = p.mesh.material as THREE.MeshBasicMaterial;
      mat.opacity = (1 - t) * 0.38;
      p.mesh.scale.setScalar(1 + t * 2.1);
      p.mesh.position.y -= dt * 1.4;
      if (p.life >= p.maxLife) {
        p.mesh.removeFromParent();
        this.trailPuffs.splice(i, 1);
      }
    }
  }

  dispose(): void {
    for (const p of this.trailPuffs) p.mesh.removeFromParent();
    this.trailPuffs.length = 0;
    this.trailGeo.dispose();
    this.trailMat.dispose();
    this.root.traverse((o) => {
      if (o instanceof THREE.Mesh) {
        o.geometry?.dispose();
        if (o.material instanceof THREE.Material) o.material.dispose();
      }
    });
    this.root.removeFromParent();
  }
}
